// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState, useCallback } from 'react'
import { Truck, Download, DollarSign, Package, CheckCircle2, AlertTriangle, Clock } from 'lucide-react'
import * as api from '../../api/client'
import { BarChart, DonutChart } from '../../components/charts/Charts'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import { carrierLabel } from '../../lib/carriers'

// D-UI-2: a funnel on every column. Each value is the text the cell shows ("$12.50" and "8.4%"
// still read as numbers, so a column with many of them gets Min/Max).
type ServiceRow = NonNullable<api.ShippingReport['by_service']>[number]
type PatientRow = NonNullable<api.ShippingReport['by_patient']>[number]
type CarrierRow = api.ShippingReport['by_carrier'][number]
const avgPer = (cost: number, count: number) => (count > 0 ? `$${(cost / count).toFixed(2)}` : null)
const PATIENT_FUNNELS: FunnelColumn<PatientRow>[] = [
  { key: 'display_name', label: 'Patient', value: pt => pt.display_name },
  { key: 'count', label: 'Shipments', value: pt => pt.count },
  { key: 'cost', label: 'Cost', value: pt => `$${pt.cost.toFixed(2)}` },
  { key: 'avg', label: 'Avg / shipment', value: pt => avgPer(pt.cost, pt.count) },
]
const CARRIER_FUNNELS: FunnelColumn<CarrierRow>[] = [
  { key: 'carrier', label: 'Carrier', value: c => carrierLabel(c.carrier) },
  { key: 'count', label: 'Orders', value: c => c.count },
  { key: 'cost', label: 'Cost', value: c => `$${c.cost.toFixed(2)}` },
  { key: 'avg', label: 'Avg / order', value: c => avgPer(c.cost, c.count) },
]

/**
 * Shipping report — current pharmacy (Sprint 18).
 *
 * Order volume + cost over a date range, time-bucketed, with a carrier
 * breakdown. Reads only local rx_shipments (no carrier API calls), so it
 * reflects shipments created through OwlRx or imported via Save-to-OwlRx.
 *
 * Date selector drives a single backend aggregation; the page never pulls
 * raw shipments client-side, keeping it fast regardless of volume.
 */
export default function ShippingReportPage() {
  // Default last 30 days.
  const today = new Date()
  const monthAgo = new Date(today.getTime() - 29 * 86400000)
  const iso = (d: Date) => d.toISOString().slice(0, 10)

  const [from, setFrom] = useState(iso(monthAgo))
  const [to, setTo] = useState(iso(today))
  // Date basis: shipped vs DELIVERED (Patrick: report on delivery dates), and
  // explicit period grouping incl. month + annual rollups.
  const [basis, setBasis] = useState<'shipped' | 'delivered'>('delivered')
  const [group, setGroup] = useState<'auto' | 'day' | 'month' | 'year'>('month')
  const [report, setReport] = useState<api.ShippingReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      setReport(await api.getShippingReport(from, to, basis, group))
    } catch (e: any) {
      setError(e?.message || 'Could not load report.')
    } finally {
      setLoading(false)
    }
  }, [from, to, basis, group])

  useEffect(() => { load() }, [load])

  // D-UI-2: column funnels for the three tables. "Share" is a share of the report's total cost,
  // so the service columns are rebuilt when that total changes.
  const totalCost = report?.totals.total_cost ?? 0
  const serviceFunnels = useMemo<FunnelColumn<ServiceRow>[]>(() => [
    { key: 'service', label: 'Service', value: sv => sv.service },
    { key: 'count', label: 'Orders', value: sv => sv.count },
    { key: 'cost', label: 'Cost', value: sv => `$${sv.cost.toFixed(2)}` },
    { key: 'avg', label: 'Avg / order', value: sv => avgPer(sv.cost, sv.count) },
    { key: 'share', label: 'Share', value: sv => `${((totalCost > 0 ? sv.cost / totalCost : 0) * 100).toFixed(1)}%` },
    { key: 'avg_delivery', label: 'Avg delivery', value: sv => (sv.avg_delivery_minutes != null ? `${Math.round(sv.avg_delivery_minutes / 60 * 10) / 10}h` : null) },
  ], [totalCost])
  const svcFx = useTableFunnels(report?.by_service, serviceFunnels)
  const patFx = useTableFunnels(report?.by_patient, PATIENT_FUNNELS)
  const carFx = useTableFunnels(report?.by_carrier, CARRIER_FUNNELS)

  const exportCsv = () => {
    if (!report) return
    const label = basis === 'delivered' ? 'Delivery period' : 'Ship period'
    const rows = [[label, 'Orders', 'Cost', 'Avg cost/order']]
    report.series.forEach(s => rows.push([
      fmtBucket(s.bucket), String(s.count), s.cost.toFixed(2),
      s.count > 0 ? (s.cost / s.count).toFixed(2) : '',
    ]))
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `shipping-report-${basis}-${from}-to-${to}.csv`
    a.click()
  }

  const fmtBucket = (b: string) => {
    if (!report) return b
    if (report.bucket === 'year') return b.slice(0, 4)
    if (report.bucket === 'month') {
      const d = new Date(b + 'T00:00:00')
      return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
    }
    return b
  }
  const t = report?.totals
  const basisWord = basis === 'delivered' ? 'delivery' : 'ship'
  const fmtBucketShort = (b: string) => {
    if (!report) return b
    if (report.bucket === 'year') return b.slice(0, 4)
    if (report.bucket === 'month') return new Date(b + 'T00:00:00').toLocaleDateString(undefined, { month: 'short' })
    // day: show just the day-of-month to keep axis readable
    return String(new Date(b + 'T00:00:00').getDate())
  }

  return (
    <div style={page}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <Truck size={22} style={{ color: 'var(--color-primary, #0d9488)' }} />
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Shipping report</h1>
      </div>
      <p style={{ marginTop: 0, color: 'var(--color-text-muted)', fontSize: 13 }}>
        Delivery volume and cost for your pharmacy, based on the date each order was {basis === 'delivered' ? 'delivered' : 'shipped'}. Reads only local records — no carrier calls.
      </p>

      {/* Date selector */}
      <div style={controls}>
        <label style={ctrlLabel}>From
          <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} style={dateInput} />
        </label>
        <label style={ctrlLabel}>To
          <input type="date" value={to} min={from} max={iso(today)} onChange={e => setTo(e.target.value)} style={dateInput} />
        </label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['7d', 6], ['30d', 29], ['90d', 89], ['1y', 364]].map(([label, days]) => (
            <button key={label as string} type="button" style={presetBtn}
              onClick={() => { setFrom(iso(new Date(today.getTime() - (days as number) * 86400000))); setTo(iso(today)) }}>
              {label}
            </button>
          ))}
          <button type="button" style={presetBtn}
            onClick={() => { setFrom(`${today.getFullYear()}-01-01`); setTo(iso(today)); setGroup('month') }}>
            This year
          </button>
          <button type="button" style={presetBtn}
            onClick={() => { setFrom('2015-01-01'); setTo(iso(today)); setGroup('year') }}>
            All time
          </button>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Dates:</span>
          {(['delivered', 'shipped'] as const).map(b => (
            <button key={b} type="button" style={{ ...presetBtn, ...(basis === b ? presetActive : null) }}
              onClick={() => setBasis(b)}>
              {b === 'delivered' ? 'Delivered' : 'Shipped'}
            </button>
          ))}
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 8 }}>Group:</span>
          {(['month', 'year', 'day'] as const).map(g => (
            <button key={g} type="button" style={{ ...presetBtn, ...(group === g ? presetActive : null) }}
              onClick={() => setGroup(g)}>
              {g === 'month' ? 'Monthly' : g === 'year' ? 'Annually' : 'Daily'}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" style={csvBtn} onClick={exportCsv} disabled={!report || report.series.length === 0}>
          <Download size={14} /> CSV
        </button>
        <button type="button" style={csvBtn} title="Per-delivery ledger: dates, order #, service, cost, patient — reconcile against the courier invoice"
          onClick={() => { window.location.href = `/api/shipments/report/audit.csv?from=${from}&to=${to}&basis=${basis}` }}>
          <Download size={14} /> Audit CSV
        </button>
      </div>

      {error && <div style={errBox}>{error}</div>}
      {loading && <div style={{ padding: 20, color: 'var(--color-text-muted)' }}>Loading…</div>}

      {report && !loading && (
        <>
          {/* Summary cards */}
          <div style={statGrid}>
            <StatCard icon={<Package size={16} />} label="Order volume" value={String(t!.order_volume)} />
            <StatCard icon={<DollarSign size={16} />} label="Total cost" value={`$${t!.total_cost.toFixed(2)}`} />
            <StatCard icon={<DollarSign size={16} />} label="Avg cost / order"
              value={t!.order_volume > 0 ? `$${(t!.total_cost / t!.order_volume).toFixed(2)}` : '—'} />
            <StatCard icon={<CheckCircle2 size={16} />} label="Delivered" value={String(t!.delivered_count)} />
            <StatCard icon={<AlertTriangle size={16} />} label="Exceptions" value={String(t!.exception_count)} />
            <StatCard icon={<Clock size={16} />} label="Avg delivery"
              value={t!.avg_delivery_minutes != null ? `${Math.round(t!.avg_delivery_minutes / 60 * 10) / 10}h` : '—'} />
          </div>

          {report.series.length === 0 ? (
            <div style={card}><div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No {basisWord} shipments in this period.
            </div></div>
          ) : (
            <>
              {/* Delivery cost per period — the headline (D-SHIP-REPORT-1) */}
              <div style={card}>
                <div style={chartHdr}>
                  Cost <span style={bucketTag}>per {report.bucket} · by {basisWord} date</span>
                  <span style={{ flex: 1 }} />
                  <span style={bucketTag}>${report.series.reduce((a, s) => a + s.cost, 0).toFixed(2)} total</span>
                </div>
                <div style={{ padding: '16px 16px 8px' }}>
                  <BarChart
                    data={report.series.map(s => ({ label: fmtBucketShort(s.bucket), sublabel: fmtBucket(s.bucket), value: s.cost }))}
                    color="#a16207"
                    valueFormat={v => `$${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toFixed(0)}`}
                  />
                </div>
              </div>

              {/* Deliveries per period */}
              <div style={card}>
                <div style={chartHdr}>
                  {basis === 'delivered' ? 'Deliveries' : 'Shipments'} <span style={bucketTag}>per {report.bucket} · by {basisWord} date</span>
                </div>
                <div style={{ padding: '16px 16px 8px' }}>
                  <BarChart
                    data={report.series.map(s => ({ label: fmtBucketShort(s.bucket), sublabel: fmtBucket(s.bucket), value: s.count }))}
                    color="var(--color-primary, #0d9488)"
                    valueFormat={v => String(Math.round(v))}
                  />
                </div>
              </div>

              {/* Two donuts side by side: cost by carrier + status mix */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                <div style={card}>
                  <div style={chartHdr}>Cost by carrier</div>
                  <div style={{ padding: 18 }}>
                    <DonutChart
                      centerLabel="total"
                      valueFormat={v => `$${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toFixed(0)}`}
                      data={report.by_carrier.map((c, i) => ({ label: carrierLabel(c.carrier), value: c.cost, color: CARRIER_COLORS[i % CARRIER_COLORS.length] }))}
                    />
                  </div>
                </div>
                <div style={card}>
                  <div style={chartHdr}>Delivery status</div>
                  <div style={{ padding: 18 }}>
                    <DonutChart
                      centerLabel="orders"
                      valueFormat={v => String(Math.round(v))}
                      data={[
                        { label: 'Delivered', value: t!.delivered_count, color: '#16a34a' },
                        { label: 'In transit', value: Math.max(0, t!.order_volume - t!.delivered_count - t!.exception_count), color: '#0369a1' },
                        { label: 'Exception', value: t!.exception_count, color: '#dc2626' },
                      ].filter(d => d.value > 0)}
                    />
                  </div>
                </div>
              </div>

              {/* D-SHIP-9: Cost by service — which services cost us the most */}
              {(report.by_service?.length ?? 0) > 0 && (
                <div style={card}>
                  <div style={chartHdr}>Cost by service <span style={bucketTag}>which services cost the most</span></div>
                  {svcFx.chips}
                  <table {...svcFx.tableProps} style={table}>
                    <thead><tr><th style={th}>Service {svcFx.funnel('service')}</th><th style={thNum}>Orders {svcFx.funnel('count')}</th><th style={thNum}>Cost {svcFx.funnel('cost')}</th><th style={thNum}>Avg / order {svcFx.funnel('avg')}</th><th style={thNum}>Share {svcFx.funnel('share')}</th><th style={thNum}>Avg delivery {svcFx.funnel('avg_delivery')}</th></tr></thead>
                    <tbody>
                      {svcFx.rows.map(sv => {
                        const share = t!.total_cost > 0 ? sv.cost / t!.total_cost : 0
                        return (
                          <tr key={sv.service}>
                            <td style={td}><b>{sv.service}</b></td>
                            <td style={tdNum}>{sv.count}</td>
                            <td style={tdNum}>${sv.cost.toFixed(2)}</td>
                            <td style={tdNum}>{sv.count > 0 ? `$${(sv.cost / sv.count).toFixed(2)}` : '—'}</td>
                            <td style={tdNum}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 70, height: 7, borderRadius: 4, background: 'var(--color-border)', overflow: 'hidden', display: 'inline-block' }}>
                                  <span style={{ display: 'block', height: '100%', width: `${Math.round(share * 100)}%`, background: '#a16207' }} />
                                </span>
                                {(share * 100).toFixed(1)}%
                              </span>
                            </td>
                            <td style={tdNum}>{sv.avg_delivery_minutes != null ? `${Math.round(sv.avg_delivery_minutes / 60 * 10) / 10}h` : '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* D-SHIP-9: Cost per patient */}
              {report.patient_summary && (
                <div style={card}>
                  <div style={chartHdr}>
                    Cost per patient
                    <span style={bucketTag}>
                      {report.patient_summary.distinct_patients} patients · avg ${report.patient_summary.avg_cost_per_patient.toFixed(2)}/patient
                      {report.patient_summary.unattributed_count > 0 &&
                        ` · $${report.patient_summary.unattributed_cost.toFixed(2)} unattributed (${report.patient_summary.unattributed_count} manual)`}
                    </span>
                  </div>
                  {patFx.chips}
                  <table {...patFx.tableProps} style={table}>
                    <thead><tr><th style={th}>Patient {patFx.funnel('display_name')}</th><th style={thNum}>Shipments {patFx.funnel('count')}</th><th style={thNum}>Cost {patFx.funnel('cost')}</th><th style={thNum}>Avg / shipment {patFx.funnel('avg')}</th></tr></thead>
                    <tbody>
                      {patFx.rows.map(pt => (
                        <tr key={pt.patient_id}>
                          <td style={td}>{pt.display_name}</td>
                          <td style={tdNum}>{pt.count}</td>
                          <td style={tdNum}>${pt.cost.toFixed(2)}</td>
                          <td style={tdNum}>{pt.count > 0 ? `$${(pt.cost / pt.count).toFixed(2)}` : '—'}</td>
                        </tr>
                      ))}
                      {patFx.rows.length === 0 && (
                        <tr><td style={td} colSpan={4}>{patFx.active ? 'No patients match the filters.' : 'No attributed shipments in this period.'}</td></tr>
                      )}
                    </tbody>
                  </table>
                  <div style={{ padding: '6px 16px 12px', fontSize: 11, color: 'var(--color-text-muted)' }}>
                    Top 25 by cost. A shipment's full cost is attributed to the patient of its primary Rx; manual packages are counted as unattributed.
                  </div>
                </div>
              )}

              {/* Carrier detail table */}
              <div style={card}>
                <div style={chartHdr}>By carrier <span style={bucketTag}>detail</span></div>
                {carFx.chips}
                <table {...carFx.tableProps} style={table}>
                  <thead><tr><th style={th}>Carrier {carFx.funnel('carrier')}</th><th style={thNum}>Orders {carFx.funnel('count')}</th><th style={thNum}>Cost {carFx.funnel('cost')}</th><th style={thNum}>Avg / order {carFx.funnel('avg')}</th></tr></thead>
                  <tbody>
                    {carFx.rows.map(c => (
                      <tr key={c.carrier}>
                        <td style={td}>{carrierLabel(c.carrier)}</td>
                        <td style={tdNum}>{c.count}</td>
                        <td style={tdNum}>${c.cost.toFixed(2)}</td>
                        <td style={tdNum}>{c.count > 0 ? `$${(c.cost / c.count).toFixed(2)}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div style={statCard}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-muted)' }}>
        {icon}<span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  )
}

const CARRIER_COLORS = ['#0d9488', '#a16207', '#7c3aed', '#0369a1', '#dc2626', '#db2777', '#65a30d', '#c2410c']

const page: React.CSSProperties = { padding: '20px clamp(16px, 3vw, 40px)', maxWidth: 'none', width: '100%', boxSizing: 'border-box' }   // D-SHIP-9: use the full screen
const controls: React.CSSProperties = { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', margin: '14px 0 18px' }
const ctrlLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }
const dateInput: React.CSSProperties = { padding: '6px 10px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)' }
const presetBtn: React.CSSProperties = { padding: '6px 10px', fontSize: 12, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', cursor: 'pointer' }
const presetActive: React.CSSProperties = { background: 'var(--color-primary, #0d9488)', color: '#fff', borderColor: 'var(--color-primary, #0d9488)' }
const csvBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', fontSize: 13, fontWeight: 600, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', cursor: 'pointer' }
const statGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }
const statCard: React.CSSProperties = { padding: '12px 14px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8 }
const card: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, marginBottom: 16, overflow: 'hidden' }
const chartHdr: React.CSSProperties = { padding: '12px 16px', borderBottom: '1px solid var(--color-border)', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }
const bucketTag: React.CSSProperties = { fontSize: 11, fontWeight: 500, color: 'var(--color-text-muted)' }
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' }
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)' }
const thNum: React.CSSProperties = { ...th, textAlign: 'right' }
const td: React.CSSProperties = { padding: '8px 16px', fontSize: 13, borderBottom: '1px solid var(--color-border)' }
const tdNum: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
const errBox: React.CSSProperties = { padding: '8px 12px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13, marginBottom: 12 }
